import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { phaseColor } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Users, DollarSign, Wallet, Library, Gauge } from "lucide-react";

export function money(v, cur = "USD") {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${cur} ${n.toLocaleString()}`;
  }
}

const blank = { name: "", role: "", daily_rate: 0, currency: "USD", capacity: 1.0 };

export default function BudgetView({ projectId, phaseColors, onCostChange }) {
  const [data, setData] = useState(null);
  const [resources, setResources] = useState([]);
  const [budgetInput, setBudgetInput] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [libResources, setLibResources] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    const [b, r] = await Promise.all([
      api.get(`/projects/${projectId}/budget`),
      api.get(`/projects/${projectId}/resources`),
    ]);
    setData(b.data);
    setResources(r.data);
    setBudgetInput(b.data.budget ?? "");
    onCostChange?.();
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBudget = async () => {
    try {
      await api.patch(`/projects/${projectId}/budget`, { budget: Number(budgetInput || 0) });
      toast.success("Budget updated");
      load();
    } catch (e) { toast.error("Could not update budget"); }
  };

  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...blank, ...r }); setEditId(r.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveRes = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, daily_rate: Number(form.daily_rate || 0), capacity: Number(form.capacity || 1) };
      if (editId) await api.put(`/resources/${editId}`, payload);
      else await api.post(`/projects/${projectId}/resources`, payload);
      setOpen(false);
      toast.success(editId ? "Resource updated" : "Resource added");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeRes = async (id) => {
    await api.delete(`/resources/${id}`);
    setOpen(false);
    toast.success("Resource removed");
    load();
  };

  const openImport = async () => {
    setImportOpen(true);
    if (libResources === null) {
      try { const { data } = await api.get("/library-resources"); setLibResources(data); }
      catch (e) { setLibResources([]); }
    }
  };

  const importOne = async (r) => {
    try {
      await api.post(`/projects/${projectId}/resources`, {
        name: r.name, role: r.role, daily_rate: r.daily_rate, currency: r.currency, capacity: r.capacity,
      });
      toast.success(`Imported ${r.name}`);
      setImportOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveToLibrary = async () => {
    if (!editId) return;
    try { await api.post(`/library-resources/from-resource/${editId}`); toast.success("Saved to library"); }
    catch (e) { toast.error("Could not save to library"); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const cur = data.currency;
  const maxPhase = Math.max(1, ...data.by_phase.map((p) => p.cost));

  return (
    <div className="space-y-6" data-testid="budget-view">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-border rounded-lg p-5">
          <p className="overline text-muted-foreground">Budget</p>
          <div className="flex items-center gap-2 mt-2">
            <Input value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)} type="number" placeholder="Set budget" className="h-9" data-testid="budget-input" />
            <Button size="sm" onClick={saveBudget} data-testid="budget-save-button">Save</Button>
          </div>
        </div>
        <div className="border border-border rounded-lg p-5">
          <p className="overline text-muted-foreground">Planned cost</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-2" data-testid="planned-cost">{money(data.planned_cost, cur)}</p>
        </div>
        <div className="border border-border rounded-lg p-5">
          <p className="overline text-muted-foreground">Budget variance</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: data.budget_variance == null ? undefined : data.budget_variance < 0 ? "#EF4444" : "#10B981" }}>
            {data.budget_variance == null ? "—" : money(data.budget_variance, cur)}
          </p>
        </div>
        <div className="border border-border rounded-lg p-5">
          <p className="overline text-muted-foreground">Budget used</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-2">{data.percent_used == null ? "—" : `${data.percent_used}%`}</p>
          {data.percent_used != null && <Progress value={Math.min(100, data.percent_used)} className="h-1.5 mt-2" />}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* resources */}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Resources & rates</h3>
            <div className="flex gap-2">
              <Button variant="outline" className="gap-2" onClick={openImport} data-testid="import-resource-button"><Library className="h-4 w-4" /> Import from library</Button>
              <Button className="gap-2" onClick={openNew} data-testid="add-resource-button"><Plus className="h-4 w-4" /> Add resource</Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Costs roll up automatically: a task's cost = working days × the rate of its assigned resource (matched by name) + any fixed cost.</p>
          <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-left">
                  <th className="px-4 py-2.5 font-semibold">Resource</th>
                  <th className="px-4 py-2.5 font-semibold">Role</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Rate/day</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Days</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.by_resource.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No resources yet. Add one to start costing the plan.</td></tr>
                )}
                {data.by_resource.map((r) => {
                  const editable = resources.find((x) => x.name === r.name);
                  return (
                    <tr key={r.name} className="border-b border-border/60 hover:bg-secondary/20 cursor-pointer" onClick={() => editable && openEdit(editable)} data-testid={`resource-row-${r.name}`}>
                      <td className="px-4 py-2.5 font-medium">{r.name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.role || "—"}</td>
                      <td className="px-4 py-2.5 text-right">{money(r.daily_rate, cur)}</td>
                      <td className="px-4 py-2.5 text-right">{r.days}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{money(r.cost, cur)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* by phase */}
        <div className="lg:col-span-2 border border-border rounded-lg p-5 h-fit">
          <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-4"><Wallet className="h-5 w-5 text-primary" /> Cost by phase</h3>
          <div className="space-y-3">
            {data.by_phase.length === 0 && <p className="text-sm text-muted-foreground">No costs yet.</p>}
            {data.by_phase.map((p, i) => (
              <div key={p.phase}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium">{p.phase}</span>
                  <span className="text-muted-foreground">{money(p.cost, cur)}</span>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(p.cost / maxPhase) * 100}%`, background: (phaseColors && phaseColors[p.phase]) || phaseColor(p.phase, i) }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="resource-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Add"} resource</DialogTitle>
            <DialogDescription>Resources are matched to tasks by name (the task's assignee).</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveRes} className="space-y-4">
            <div>
              <Label htmlFor="res-name">Name (must match task assignee)</Label>
              <Input id="res-name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. BGM Team" data-testid="resource-name-input" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="res-role">Role</Label>
                <Input id="res-role" value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="resource-role-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="res-rate">Daily rate</Label>
                <Input id="res-rate" type="number" value={form.daily_rate} onChange={(e) => set("daily_rate", e.target.value)} data-testid="resource-rate-input" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="res-capacity">Capacity (FTE / day)</Label>
              <Input id="res-capacity" type="number" min="0.1" step="0.1" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="1.0" data-testid="resource-capacity-input" className="mt-1.5 w-32" />
              <p className="text-[11px] text-muted-foreground mt-1">Used for resource leveling — 1.0 = one full-time person. Over-allocation is flagged when daily load exceeds this.</p>
            </div>
            <DialogFooter className="flex sm:justify-between gap-2">
              {editId && (
                <Button type="button" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2" onClick={() => removeRes(editId)} data-testid="resource-delete-button">
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                {editId && <Button type="button" variant="outline" className="gap-2" onClick={saveToLibrary} data-testid="resource-to-library-button"><Library className="h-4 w-4" /> Save to library</Button>}
                <Button type="submit" disabled={saving} data-testid="resource-save-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="import-resource-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Library className="h-5 w-5 text-primary" /> Import from library</DialogTitle>
            <DialogDescription>Reuse a saved resource in this project.</DialogDescription>
          </DialogHeader>
          {libResources === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : libResources.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No saved resources yet. Add some in the Library → Resources tab.</p>
          ) : (
            <div className="space-y-2">
              {libResources.map((r) => (
                <button key={r.id} onClick={() => importOne(r)} className="w-full text-left border border-border rounded-lg p-3 hover:border-foreground/30 transition-colors flex items-center justify-between gap-3" data-testid={`import-libres-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.role || "—"}</p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground shrink-0">
                    <p className="inline-flex items-center gap-1"><DollarSign className="h-3 w-3" />{r.daily_rate}/day</p>
                    <p className="inline-flex items-center gap-1"><Gauge className="h-3 w-3" />{r.capacity} FTE</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
